import { withDefault, definedMap } from "collection-utils";

import {
    isPrimitiveType,
    ComponentKind,
    TableColumn,
    ComponentDescription,
    ActionKind,
    LegacyPropertyDescription,
    PropertyKind,
    InputOutputTables,
    getTableColumnDisplayName,
    getTableColumn,
    MutatingScreenKind,
    TableGlideType,
    makeEmptyComponentDescription,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { assert } from "../../Support";
import {
    ComponentDescriptor,
    EnumPropertyCase,
    ComponentBuilder,
    PropertyDescriptor,
    ComponentConfiguratorContext,
} from ".";
import { ComponentHandlerBase } from "./handler";
import {
    getColumnProperty,
    getEnumProperty,
    makeColumnProperty,
    makeStringProperty,
    makeEnumProperty,
} from "./property-utils";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { withColumnFormat } from "../formulas";
import { makeCaptionStringPropertyDescriptor, makeTextPropertyDescriptor } from "./descriptor-utils";
import { withCaption, LinkAppearance, makeText } from "../builder-utils";

const ComponentKindLink: ComponentKind = "link";

interface LinkComponentDescription extends ComponentDescription {
    readonly propertyName: LegacyPropertyDescription;
    readonly caption: LegacyPropertyDescription | undefined;
    readonly appearance: LegacyPropertyDescription;
}

interface AppearanceData extends EnumPropertyCase<LinkAppearance> {
    uiFunction: string;
}

export const linkAppearances: readonly AppearanceData[] = [
    {
        value: LinkAppearance.PageTitle,
        label: "Page title",
        uiFunction: "pageTitleSmartLink",
    },
    {
        value: LinkAppearance.LastPathComponent,
        label: "Last path component",
        uiFunction: "lastPathComponentSmartLink",
    },
    {
        value: LinkAppearance.ShortURL,
        label: "Short URL",
        uiFunction: "shortURLSmartLink",
    },
    {
        value: LinkAppearance.FullURL,
        label: "Full URL",
        uiFunction: "urlSmartLink",
    },
];

const allAppearances: readonly AppearanceData[] = [
    ...linkAppearances,
    {
        value: LinkAppearance.CaptionAsTitle,
        label: "Caption as title",
        uiFunction: "captionAsTitleSmartLink",
    },
];

function getAppearanceData(appearance: LinkAppearance): AppearanceData {
    return withDefault(
        allAppearances.find(a => a.value === appearance),
        allAppearances[0]
    );
}

export function linkPropertyDescriptor(
    name: string,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isDefaultCaption: boolean
): PropertyDescriptor {
    return makeTextPropertyDescriptor(name, "Link", "Enter URL", true, mutatingScreenKind, {
        preferredNames: ["link", "url"],
        columnFirst: true,
        searchable: false,
        preferredType: "uri",
        isDefaultCaption,
    });
}

export class LinkComponentHandler extends ComponentHandlerBase<LinkComponentDescription> {
    constructor() {
        super(ComponentKindLink);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: LinkComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = getColumnProperty(desc.propertyName);
        const appearance = getEnumProperty<LinkAppearance>(desc.appearance);
        if (appearance === undefined) return b.errorUI;

        // FIXME: We shouldn't be using the formatted value here, should we?
        const link = makeText(builder, desc.propertyName, tables.input, true, true, true);
        if (link === undefined) return b.emptyUI;

        const linkColumn = definedMap(propertyName, n => getTableColumn(tables.input, n));

        const onTap = builder.makeSequenceFromActions(
            [{ kind: ActionKind.OpenLink }],
            tables,
            undefined,
            outputContext,
            mutatingScreenKind,
            false
        );
        const { uiFunction } = getAppearanceData(appearance);

        return withCaption(builder, tables.input, desc.caption, [], caption => {
            let child: SerializedComponent;
            if (appearance === LinkAppearance.FullURL) {
                child = b.functionCall(uiFunction, [
                    withColumnFormat(builder, b.getContext, tables.input, linkColumn),
                    caption,
                    onTap,
                ]);
            } else {
                child = b.functionCall(uiFunction, [caption, onTap]);
            }

            const ui = b.ifIsNotEmpty(b.getContext, child);
            return b.bindContextToValue(ui, link);
        });
    }

    public getDescriptor(
        _desc: LinkComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<LinkComponentDescription> {
        return {
            name: "Link",
            description: "A link that opens when you tap it",
            img: "componentLink",
            group: "Buttons",
            helpUrl: getDocURL("link"),
            properties: [
                linkPropertyDescriptor("propertyName", mutatingScreenKind, true),
                {
                    kind: PropertyKind.Enum,
                    property: { name: "appearance" },
                    label: "Label",
                    menuLabel: "Show as",
                    cases: allAppearances,
                    defaultCaseValue: LinkAppearance.PageTitle,
                    section: PropertySection.Design,
                    visual: "dropdown",
                },
                makeCaptionStringPropertyDescriptor("Link", false, mutatingScreenKind),
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }

    public static defaultComponent(column: TableColumn): LinkComponentDescription {
        assert(isPrimitiveType(column.type));
        return {
            ...makeEmptyComponentDescription(ComponentKindLink),
            propertyName: makeColumnProperty(column.name),
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            appearance: makeEnumProperty(LinkAppearance.PageTitle),
        };
    }
}
